import { useEffect, useState } from 'react'

// Same key and event the listaa code uses (src/listaa/lib/api/apiClient.ts, app/pages/Login.tsx).
const TOKEN_KEY = 'venuelista_token'
const AUTH_CHANGED_EVENT = 'venuelista:auth-changed'

// Whether a session token is stored. Read after mount (the prerendered HTML has no session) and
// re-read on sign-in, focus, and changes from other tabs.
// Members and other non-venue accounts sign in on listaa.com, not here. The account type comes
// from listaa's saved session (src/listaa/store/authStore.ts, key venuelista_auth).
function isNonVenueSession() {
  const type = JSON.parse(localStorage.getItem('venuelista_auth') || 'null')?.state?.user?.accountType
  return Boolean(type) && type !== 'venue'
}

export function useSignedIn() {
  const [signedIn, setSignedIn] = useState(false)
  useEffect(() => {
    const check = () => {
      try { setSignedIn(Boolean(localStorage.getItem(TOKEN_KEY)) && !isNonVenueSession()) } catch { setSignedIn(false) }
    }
    check()
    window.addEventListener(AUTH_CHANGED_EVENT, check)
    window.addEventListener('storage', check)
    window.addEventListener('focus', check)
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, check)
      window.removeEventListener('storage', check)
      window.removeEventListener('focus', check)
    }
  }, [])
  return signedIn
}

// The signed-in venue's name and logo for the header. The API client loads only once signed in,
// so the home page bundle stays light. Returns undefined while loading, null if it failed.
// Kept for the session so moving between pages doesn't flash the loading state again.
let cachedVenue
export function useMyVenue(signedIn) {
  const [venue, setVenue] = useState(cachedVenue)
  useEffect(() => {
    if (!signedIn) return setVenue(undefined)
    if (cachedVenue !== undefined) return setVenue(cachedVenue)
    let live = true
    import('@/lib/api')
      .then((m) => m.venuesApi.getMyVenue())
      .catch(() => null)
      .then((v) => { cachedVenue = v ?? null; if (live) setVenue(cachedVenue) })
    return () => { live = false }
  }, [signedIn])
  return venue
}

// Signs out through listaa's auth store (clears the token and its saved session), then reloads
// home so no page keeps the old account in memory.
export async function signOut() {
  try {
    const { useAuthStore } = await import('@/store/authStore')
    useAuthStore.getState().logout()
  } catch {
    try { localStorage.removeItem(TOKEN_KEY) } catch { /* storage blocked */ }
  }
  window.location.assign('/')
}
